//#region libraries
//react
import React, { useEffect, useState, useMemo } from "react";

// custom components
import { Title as CustomTitle, NewScreen, Card } from "_atoms";
import { StarRating, TableHeader, TableValue, ReviewName, StarList } from "_molecules";
import { ClickableTable, CustomHeatMap } from "_organisms";

// bootstrap
import { Row, Col } from "react-bootstrap";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectFirebaseUser, selectUserDb, getDbInfo } from "_redux/slices/main-screen/user";
import {
  getScoreCard,
  selectAverages,
  getAverages,
  selectAirportInformation,
  getAirportInformation,
  selectReviewsAverage,
  selectReviewsListObject,
  getOverallReviewScores,
  selectSelectedScore,
  clearSelectedScore,
  getReviewsAverages
} from "_redux/slices/analytics/reviews";
import * as reviewsSlice from "_redux/slices/analytics/reviews";
import { reset as resetBeacons } from "_redux/slices/beacons/beacons";
// import { getWaitingTimes } from "_redux/slices/beacons/beacons"; // Waiting Times tab hidden

// external libraries
import {
  Title as TremorTitle,
  Subtitle,
  Text,
  Toggle,
  ToggleItem,
  Button as TremorButton,
  Metric,
  Flex,
  Bold,
  List,
  ListItem,
  TabList,
  Tab,
} from "@tremor/react";
import { DateRangePicker } from "@tremor/react";

// screens
// import WaitingTimes from "./waiting-times/index"; // Waiting Times tab hidden

import ReviewList from "./review-list/index";
import { lookupIsLoading } from "_utils/statusUtil";
import ReviewControlPanel from "./review-control-panel";
import SurveysAnswers from "./survey-answers";
import SurveyStats from "./survey-stats";
import PerformanceReport from "./performance-report";

//#endregion
// const airportsWithWaitingTime = ["BRS", "GLA", "BFS", "ORK", "JER", "DUB", "SNN"]; // Waiting Times tab hidden

function getDateOneMonthAgo(currentDate) {
  const currentTimestamp = currentDate.getTime();
  const thirtyDaysAgoTimestamp = currentTimestamp - 2592000000;
  const thirtyDaysAgo = new Date(thirtyDaysAgoTimestamp);
  return thirtyDaysAgo;
}

const Analytics = () => {


  const waitingTimeLabelMap = {
    "less than 15mins": "Waiting less than 15 mins",
    "15 - 30 mins": "Waiting between 15-30 mins",
    "more than 30 mins": "Waiting more than 30 mins"
  };

  //#region states
  const [tab, setTab] = useState(1); // testing w support desk

  // date value
  const [dateRange, setDateRange] = useState([
    getDateOneMonthAgo(new Date()),
    new Date(),
  ]);

  // table filter
  const [allBookings, setAllBookings] = useState(true);
  const dateRangeToUse = allBookings
    ? [
      new Date(0), // get earliest time possible
      new Date(),
    ]
    : dateRange;

  // redux
  const dispatch = useDispatch();
  const dbUser = useSelector(selectUserDb);
  const firebaseUser = useSelector(selectFirebaseUser);
  const averages = useSelector(selectAverages);
  const average = useSelector(selectReviewsAverage);
  const airportInformation = useSelector(selectAirportInformation);
  const selectedHeatMapData = useSelector(selectSelectedScore); // selected time and day period from heat map
  const reviewsList = useSelector(selectReviewsListObject(dbUser));

  const airportInformationIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, ["getAirportInformation"])
  );
  //#endregion
  //#region useEffects
  //reset Beacons and review slice
  useEffect(() => {
    dispatch(resetBeacons());
    dispatch(reviewsSlice.reset());
  }, []);


  useEffect(() => {
    if (dbUser && dateRangeToUse[0] && dateRangeToUse[1]) {

      dispatch(getReviewsAverages({
        iata: dbUser.details.authority,
        startDate: dateRangeToUse[0].toISOString().split("T")[0],
        endDate: dateRangeToUse[1].toISOString().split("T")[0],
        airline: dbUser.details.type === "AIRLINE"
      }))
    }
  }, [dateRange, dbUser])

  // getting averages and waiting times
  useEffect(() => {
    // if db user exsists get reviews info
    if (dbUser) {
      //heatmap data for both airline and airports
      dispatch(getOverallReviewScores({
        iata: dbUser.details.authority,
        startDate: dateRangeToUse[0].toISOString().split("T")[0],
        endDate: dateRangeToUse[1].toISOString().split("T")[0],
        airline: dbUser.details.type === "AIRLINE"
      }));
      dispatch(
        getScoreCard({
          iata: dbUser.details.authority,
          startDate: dateRangeToUse[0].toISOString().split("T")[0],
          endDate: dateRangeToUse[1].toISOString().split("T")[0],
          allBookings: allBookings,
          airline: dbUser.details.type === "AIRLINE"
        })
      );

      // Waiting Times tab hidden — beacon wait-time fetch disabled for now
      // if (dbUser.details.type !== "AIRLINE") {
      //   dispatch(getWaitingTimes({ iata: dbUser.details.authority }));
      // }
      if (dbUser.details.type === "AIRLINE") {
        //THIS CAUSE CONFLICT IN STATE for airport account
        dispatch(
          getAverages({
            iata: dbUser.details.authority,
            airline: true,
          })
        );

      }


      // check if need to get airport information
      if (
        airportInformation != undefined &&
        airportInformation[dbUser.details.authority] == null
        && dbUser.details.type !== "AIRLINE"
      ) {

        dispatch(getAirportInformation(dbUser.details.authority)); // get airport information for display
      }
    } else {
      // get db info
      dispatch(getDbInfo(firebaseUser.uid));
    }
  }, [dbUser]);


  useEffect(() => {
    // if db user exists get reviews info
    if (dbUser && dbUser.details.authority !== "ALL") {

      if (dateRange[1] == null) {
        dispatch(
          getScoreCard({
            iata: dbUser.details.authority,
            startDate: dateRangeToUse[0].toISOString().split("T")[0],
            endDate: dateRangeToUse[0].toISOString().split("T")[0],
            allBookings: allBookings,
          })
        );


      } else {
        dispatch(
          getScoreCard({
            iata: dbUser.details.authority,
            startDate: dateRangeToUse[0].toISOString().split("T")[0],
            endDate: dateRangeToUse[1].toISOString().split("T")[0],
            allBookings: allBookings,
          })
        );

        dispatch(getReviewsAverages({
          iata: dbUser.details.authority,
          startDate: dateRangeToUse[0].toISOString().split("T")[0],
          endDate: dateRangeToUse[1].toISOString().split("T")[0],
          airline: dbUser.details.type === "AIRLINE"
        }))
      }
    }
  }, [dateRange, allBookings]);
  //#endregion

  //#region score card
  // overall score averages for tile
  const overallTileScores = [
    {
      name: "Assistance Quality",
      stat: dbUser && averages ? averages.quality : "N/A",
    },
    {
      name: "Staff Peformance",
      stat: dbUser && averages ? averages.staff : "N/A",
    },
    {
      name: "Overall",
      stat: dbUser && averages ? averages.overall : "N/A",
    },
    {
      name: "Reviews",
      stat: dbUser && averages ? averages.total : "N/A",
    }
  ];

  const airlineOverallTileScores = [
    {
      name: "Boarding",
      stat: dbUser && averages ? averages.boarding : "N/A",
    },
    {
      name: "On board",
      stat: dbUser && averages ? averages.onboard : "N/A",
    },
    {
      name: "Overall",
      stat: dbUser && averages ? averages.overall : "N/A",
    },
    {
      name: "Reviews",
      stat: dbUser && averages ? averages.total : "N/A",
    }
  ];


  const columns = useMemo(() => {
    const cols = [
      {
        Header: <TableHeader title={''} />,
        accessor: "count",
        Cell: ({ cell: { value } }) => <p>{value}</p>
      },
      {
        Header: <TableHeader title={'Name'} />,
        accessor: "name",
        Cell: ({ cell: { value } }) => <TableValue title={value} />
      },
      {
        Header: <TableHeader title={'Flight Date'} />,
        accessor: "date",
        Cell: ({ cell: { value } }) => <TableValue title={value} />
      },
      {
        Header: <TableHeader title={'Flight'} />,
        accessor: "flightNumber",
        Cell: ({ cell: { value } }) => <TableValue title={value} />
      },
      {
        Header: (
          <TableHeader
            title={
              dbUser?.details?.type === 'AIRLINE'
                ? 'Boarding Assistance'
                : 'Assistance'
            }
          />
        ),
        accessor:
          dbUser?.details?.type === 'AIRLINE'
            ? 'boarding'
            : 'assistanceQuality',
        Cell: ({ cell: { value } }) => <StarRating stars={value} />
      },
      {
        Header: (
          <TableHeader
            title={
              dbUser?.details?.type === 'AIRLINE'
                ? 'Onboard Assistance'
                : 'Staff Assistance'
            }
          />
        ),
        accessor:
          dbUser?.details?.type === 'AIRLINE'
            ? 'onboard'
            : 'staffAssistance',
        Cell: ({ cell: { value } }) => <StarRating stars={value} />
      },
      {
        Header: <TableHeader title={'Comments'} />,
        accessor: "comments",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={false} overall={true} />
        )
      }
    ];

    if (dbUser?.details.type === 'AIRPORT') {
      cols.push({
        Header: <TableHeader title={'Waiting Time'} />,
        accessor: 'waitingTime',
        Cell: ({ cell: { value } }) => <TableValue title={value} />
      });
    }

    return cols;
  }, [dbUser]);

  //#endregion



  return (
    <NewScreen>
      {/* Title & Subtitle */}
      <Row>
        <Col>
          <CustomTitle
            subtitle={"Feedback Analytics"}
            description={"View analytics on feedback from your passengers"}
          />
        </Col>
      </Row>

      {/* Reviews & Waiting times tab switcher */}
      <TabList
        defaultValue="1"
        onValueChange={(value) => setTab(value)}
        className="mt-6"
      >
        {dbUser && dbUser.details && ["AIRPORT", "AIRLINE"].includes(dbUser.details.type) ? (
          <Tab value="1" text="Review Analytics" />
        ) : (
          <></>
        )}
        {dbUser && dbUser.details && ["AIRPORT", "AIRLINE"].includes(dbUser.details.type) ? (
          <Tab value="2" text="Review List" />
        ) : (
          <></>
        )}
        {/* Waiting Times tab hidden for now
        {dbUser && dbUser.details && dbUser.details.type != "AIRLINE" && ["DUB", "BFS", "ORK", "SNN"].includes(
          dbUser.details.authority
        ) ? (
          <Tab value="3" text="Waiting Times" />
        ) : (
          <></>
        )}
        */}
        {dbUser && dbUser.details && dbUser.details.type === "AIRPORT" ? <Tab value="4" text="Survey Builder" /> : <></>}
        {dbUser && dbUser.details && dbUser.details.type === "AIRPORT" ? <Tab value="5" text="Surveys" /> : <></>}
        {dbUser && dbUser.details && dbUser.details.type === "AIRPORT" ? <Tab value="7" text="Survey Delivery" /> : <></>}
        {dbUser && dbUser.position === "REPORT" ? <Tab value="6" text="Performance Report" /> : <></>}
        {/* dbUser && dbUser.details && dbUser.details.type != 'AIRLINE' ? <Tab value="4" text="Waiting Areas" /> : <></> */}
      </TabList>

      {
        // feedback tab
        tab == 1 && (
          <>
            {/* Filters ( Date Range Picker) */}
            <Row className="mt-3">
              <Flex>
                <Toggle
                  color="zinc"
                  defaultValue={allBookings}
                  onValueChange={(value) => setAllBookings(value)}
                  marginTop="mt-2.5"
                >
                  <ToggleItem value={true} text={"All Reviews"} />
                  <ToggleItem value={false} text={"By Flight Date"} />
                </Toggle>
                {!allBookings && (
                  <DateRangePicker
                    onValueChange={(value) => {
                      setDateRange(value);
                    }}
                    value={dateRange}
                    enableDropdown={true}
                    maxWidth="max-w-lg"
                    dropdownPlaceholder={"Last 30 Days"}
                  />
                )}
              </Flex>
            </Row>

            {/* Airport Profile */}
            <Row className="mt-4">
              {/* Airport Overall Scores */}
              <Col xs={3} className="h-100">
                <Card>
                  {/* Overall Score & Airport Name */}
                  <TremorTitle>Overall PRM Rating</TremorTitle>
                  {airportInformationIsLoading ? (
                    <Row className="justify-content-md-center mt-3">
                      {/* <CustomSpinner /> */}
                    </Row>
                  ) : (
                    <>
                      <Subtitle>
                        {airportInformation &&
                          airportInformation[dbUser.details.authority]
                          ? airportInformation[dbUser.details.authority].name
                          : ""}
                      </Subtitle>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Metric marginTop="mt-1.5">
                          {dbUser && dbUser.details.authority !== "ALL" && averages
                            ? averages.overall
                            : "Not Available"}
                        </Metric>
                        <div className="mt-3" style={{ marginLeft: 12 }}>
                          {dbUser && dbUser.details.authority !== "ALL" && average ? (
                            <StarRating stars={average} />
                          ) : (
                            ""
                          )}
                        </div>
                      </div>

                      {dbUser && dbUser.details.type == "AIRLINE" ? (
                        <>
                          <Flex marginTop="mt-3">
                            <Text>
                              <Bold>{"Type"}</Bold>
                            </Text>
                            <Text>
                              <Bold>{"Score"}</Bold>
                            </Text>
                          </Flex>

                          {/* Seperation */}
                          <List>
                            {airlineOverallTileScores.map((value) => (
                              <ListItem key={value.name}>

                                <Flex justify="space-between">
                                  <Text truncate>{value.name}</Text>
                                  <Text>{value.stat}</Text>
                                </Flex>

                              </ListItem>
                            ))}
                          </List>
                        </>
                      ) : (
                        <>
                          <Flex marginTop="mt-3">
                            <Text>
                              <Bold>{"Type"}</Bold>
                            </Text>
                            <Text>
                              <Bold>{"Score"}</Bold>
                            </Text>
                          </Flex>

                          {/* Seperation */}
                          <List>
                            {dbUser && averages && averages.waitingTime !== undefined && (
                              <>
                                {(() => {
                                  const validEntries = Object.entries(averages.waitingTime)
                                    .filter(([_, count]) => count !== null && count !== undefined);

                                  const total = validEntries.reduce((acc, [_, curr]) => acc + Number(curr), 0);

                                  return validEntries.map(([label, count], index) => {
                                    const percentage =
                                      total > 0 ? ((Number(count) / total) * 100).toFixed(1) + "%" : "0%";

                                    return (
                                      <ListItem key={index}>
                                        <Flex justify="space-between">
                                          <Text>{waitingTimeLabelMap[label.trim()] || label.trim()}</Text>
                                          <Text>{percentage}</Text>
                                        </Flex>
                                      </ListItem>
                                    );
                                  });
                                })()}
                              </>


                            )}
                            {overallTileScores.map((value) => (
                              <ListItem key={value.name}>
                                <Flex justify="space-between">
                                  <Text truncate>{value.name}</Text>
                                  <Text>{value.stat}</Text>
                                </Flex>
                              </ListItem>
                            ))}
                          </List>

                        </>
                      )}
                    </>
                  )}
                </Card>
              </Col>

              {/* Heat Map */}
              {dbUser && ["AIRPORT", "AIRLINE"].includes(dbUser.details.type) ? (

                <Col >
                  <Card className="h-100">
                    <Row>

                      <TremorTitle>Passenger Feedback Trends</TremorTitle>
                      <Text>
                        {
                          "View how passengers have reviewed your airport over the last period"
                        }
                      </Text>

                      <div className="mt-3">
                        <CustomHeatMap />

                      </div>
                    </Row>
                  </Card>
                </Col>
              ) : <></>}
            </Row>

            {/* Review List */}
            <Row className="mt-3" style={{ paddingBottom: 100 }}>
              <Col xs={3}>
                <Card>
                  {
                    <>
                      {selectedHeatMapData == null ? (
                        <Text>
                          Learn how passengers are experiencing your services at
                          specific times and days
                        </Text>
                      ) : (
                        <Subtitle>
                          {selectedHeatMapData.day +
                            " " +
                            selectedHeatMapData.time}
                        </Subtitle>
                      )}

                      {selectedHeatMapData && (
                        <StarList data={selectedHeatMapData} />
                      )}
                    </>
                  }
                </Card>
              </Col>
              <Col>
                <Card>
                  <Row>
                    <Flex>
                      <div>
                        <TremorTitle>Heatmap Reviews</TremorTitle>
                        <Text>
                          {
                            "View a filtered list of reviews from the same time period by clicking the heatmap"
                          }
                        </Text>
                      </div>
                      <div>
                        <TremorButton
                          size={"xs"}
                          variant="secondary"
                          disabled={selectedHeatMapData == null ? true : false}
                          onClick={() => {
                            dispatch(clearSelectedScore());
                          }}
                        >
                          Clear Heat Map Filter
                        </TremorButton>
                      </div>
                    </Flex>
                  </Row>

                  <div className="mt-5">
                    <ClickableTable
                      columns={columns}
                      data={reviewsList}
                      handleClick={() => { }}
                    //   status={status}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )
      }

      {/* Review List */}
      {tab == 2 && <>{dbUser ? <ReviewList /> : ""}</>}

      {/* Waiting Times tab hidden for now
      {tab == 3 && (
        <>
          {dbUser &&
            dbUser.details &&
            dbUser.details.authority &&
            airportsWithWaitingTime.includes(
              dbUser.details.authority
            ) ? (
            <>
              <WaitingTimes />
            </>
          ) : (
            ""
          )}
        </>
      )}
      */}

      {/* ReviewControlPanel */}
      {tab == 4 && <ReviewControlPanel />}

      {/*Survey answers*/}
      {dbUser.details.type === "AIRPORT" && tab == 5 && <SurveysAnswers />}
      {/* Survey delivery (sent vs answered) */}
      {dbUser.details.type === "AIRPORT" && tab == 7 && <SurveyStats />}
      {/* Performance Report */}
      {dbUser.details.type === "AIRPORT" && tab == 6 && <PerformanceReport />}

    </NewScreen>
  );
};

export default Analytics;